import { Injectable, EventEmitter } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Business, CostReviewEstimateType, CULTURE_ID, EstimateType, SearchType, Status } from '../models/enum';
import { Constants } from '../constants/constants';
import { GetCostReviewListRequest } from '../models/GetCostReviewListRequest';
import { BehaviorSubject, Observable, Subject, take } from 'rxjs';
import { CostReviewParameters } from '../models/CostReviewParameters';
import { Router } from '@angular/router';
import { ApiMethodsService } from './api-methods.service';
import { GetCostReviewListResponse } from '../models/GetCostReviewListResponse';
import { CommonFunctionsService } from './common-functions.service';
import { ReviewDetails } from '../models/ReviewDetails';
import { TRRThresholds } from '../models/TRRThresholds';
import { TrrDialogComponent } from '../components/dialog/trr-dialog/trr-dialog.component';
import { DenyDialogOutput, DialogInputBase, TRRDialogOutput } from '../models/DialogData';
import { MatDialog } from '@angular/material/dialog';
import { CostReviewApproveRequest, CostReviewDenyRequest, CostReviewUpdateTRRStatusRequest } from '../models/CostReviewOperationsRequest';
import { MessageDialogService } from './message-dialog.service';
import { EmailAttributes } from '../models/CostReviewOperationsResponse';
import { UserInfo } from '../models/UserInfo';
import { DenyDialogComponent } from '../components/dialog/deny-dialog/deny-dialog.component';
import { ReviewHeaderData } from '../models/ReviewHeaderData';
import { MatTableDataSource } from '@angular/material/table';
import { APIErrors } from '../models/MTError';
import { ToasterNotificationService } from './toaster-notification.service';
import { DatePipe } from '@angular/common';
import { ReviewDetailTabsService } from '../lib/review-detail-tabs.service';
import { ActionNeededField } from '../models/ActionNeededFieldChange';
import { AllMasterData } from '../models/AllMasterData';
import { CommonDataService } from './common-data.service';
import { MatPaginator } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { NewCostReviewAttributes_SOL } from '../models/NewCostReviewAttributes_SOL';
import { CostReviewDatabaseRecord } from '../models/CostReviewDatabaseRecord';
import { CostReviewAttributes } from '../models/CostReviewAttributes';
import { CostReviewFireEnUsAttributes } from '../models/CostReviewFireEnUsAttributes';
import { CostReviewSecurityEnUsAttributes } from '../models/CostReviewSecurityEnUsAttributes';

@Injectable({
  providedIn: 'root'
})
export class ReviewService {

  reviewDetailNavigate: EventEmitter<string> = new EventEmitter<string>();
  requestObj: GetCostReviewListRequest = {
    status: "",
    business_44OP: "",
    estimateType: "",
    isallreviews: true,
    pageNo: 1,
    searchText: "",
    searchType: SearchType.EstimateID
  };

  private _business: string = '';

  totalNumberOfReviews: number = -1;
  showReviewCountChanged: boolean = false;
  private showReviewCountChangedSubject = new BehaviorSubject<boolean>(false);
  showReviewCountChanged$ = this.showReviewCountChangedSubject.asObservable();
  isRefreshPaused: boolean = false;
  previousReviewCount: number = 0;
  private refreshReviewList = new BehaviorSubject<boolean>(false);
  refreshReviewList$ = this.refreshReviewList.asObservable();
  costReviewParameters: CostReviewParameters[] = [];

  filterForm: FormGroup;
  selectedReview: ReviewDetails = new ReviewDetails();
  excludedEstimateSubSegmentTypesforTRR: number[] = [Constants.TWO, Constants.THREE];
  trrThresholds: TRRThresholds = new TRRThresholds();
  reviewHeaderData: ReviewHeaderData = new ReviewHeaderData();
  currentPageReviewList: ReviewDetails[] = [];
  reviewList: ReviewDetails[] = new Array<ReviewDetails>();
  dataSource = new MatTableDataSource<ReviewDetails>(this.reviewList);
  totalAssociatedPages: number = 0;
  currentPageIndex: number = 0;
  costReviewDetails: CostReviewDatabaseRecord = new CostReviewDatabaseRecord();
  approvedSellsPrice: number = 0;
  newCostReviewObject: NewCostReviewAttributes_SOL = new NewCostReviewAttributes_SOL();

  private readonly dataRefreshed$ = new Subject<void>();
  readonly onDataRefreshed$: Observable<void> = this.dataRefreshed$.asObservable();

  constructor(private router: Router,
    private apiMethodsService: ApiMethodsService,
    private commonFunctionsService: CommonFunctionsService,
    private readonly dialog: MatDialog,
    private readonly messageService: MessageDialogService,
    private readonly notificationService: ToasterNotificationService,
    private readonly datePipe: DatePipe,
    private readonly commonDataService: CommonDataService,
    private readonly translate: TranslateService,
    private readonly reviewDetailTabsService: ReviewDetailTabsService
  ) {
    this.filterForm = new FormGroup({
      status: new FormGroup({
        open: new FormControl(true),
        approved: new FormControl(false),
        denied: new FormControl(false)
      }),
      type: new FormGroup({
        '44OP': new FormControl(true),
        '2600': new FormControl(true),
        '5200': new FormControl(true)
      }),
      business: new FormGroup({
        fire: new FormControl(true),
        security: new FormControl(true),
        automation: new FormControl(true),
        electrical: new FormControl(true),
        mechanical: new FormControl(true)
      }),
      reviewer: new FormControl('my'),
      searchType: new FormControl(SearchType.EstimateID),
      searchText: new FormControl(''),
    });
  }

  private debounceTimeout: ReturnType<typeof setTimeout> | null = null;


  //#region LIST AND SYNC

  setShowReviewCountChanged(value: boolean) {
    this.showReviewCountChanged = value;
    this.showReviewCountChangedSubject.next(value);
  }

  scheduleReviewDataRefresh() {
    const debounceDelay = Constants.FIVE_MINUTES_IN_MILISECONDS;
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }
    this.debounceTimeout = setTimeout(() => {
      if (this.isRefreshPaused) {
        this.scheduleReviewDataRefresh();
        return;
      }
      this.previousReviewCount = this.totalNumberOfReviews;
      this.requestReviewDataforRefresh(this.currentPageIndex);
      this.scheduleReviewDataRefresh();
    }, debounceDelay);
  }

  private requestReviewDataforRefresh(pageNo?: number) {

    const isReviewRoute = /^\/dashboard\/review(\/|$)/.test(this.router.url);
    if (isReviewRoute) {
      this.requestReviewData(pageNo);
      return;
    }
    else {
      this.updateRequestObjFromForm();
      this.requestObj.pageNo = pageNo ? pageNo + Constants.ONE : Constants.ONE;
      this.apiMethodsService.getReviewList(this.requestObj)
        .pipe(take(Constants.ONE))
        .subscribe({
          next: (result: GetCostReviewListResponse) => {
            if (result && result.reviewList && result.reviewList.length) {
              this.totalNumberOfReviews = result.totalCount;

              if (this.previousReviewCount !== Constants.MINUS_ONE && this.totalNumberOfReviews !== this.previousReviewCount) {
                const isReviewPage = this.router.url.includes(Constants.REVIEW_ROUTE_CHECK);
                this.setShowReviewCountChanged(!isReviewPage);
                this.refreshReviewList.next(true);
              }
            }
            else {
              this.totalNumberOfReviews = Constants.MINUS_ONE;
            }
          },
          error: () => {
            this.totalNumberOfReviews = Constants.MINUS_ONE;
          },
        });
    }
  }

  requestReviewData(pageNo?: number) {
    this.requestObj.pageNo = pageNo ? pageNo + Constants.ONE : Constants.ONE;
    const isReviewRoute = /^\/dashboard\/review(\/|$)/.test(this.router.url);
    if (isReviewRoute) {
      this.commonFunctionsService.startSpinner();
    }
    this.apiMethodsService.getReviewList(this.requestObj)
      .pipe(take(Constants.ONE))
      .subscribe({
        next: (result: GetCostReviewListResponse) => {
          if (result && result.reviewList && result.reviewList.length) {
            this.currentPageReviewList = result.reviewList;
            this.dataSource.data = this.mapResponseToUserData(result.reviewList);
            this.totalNumberOfReviews = result.totalCount;
            this.totalAssociatedPages = Math.ceil(this.totalNumberOfReviews / 100);
            this.selectedReview = new ReviewDetails();
            this.dataSource._updateChangeSubscription();

          } else {
            this.dataSource.data = new Array<ReviewDetails>();
            this.totalNumberOfReviews = Constants.MINUS_ONE;
          }
          if (this.router.url.includes(Constants.REVIEW_ROUTE_CHECK)) {
            this.commonFunctionsService.stopSpinner();
          }
        },
        error: () => {
          this.dataSource.data = new Array<ReviewDetails>();
          this.totalNumberOfReviews = Constants.MINUS_ONE;
          if (this.router.url.includes(Constants.REVIEW_ROUTE_CHECK)) {
            this.commonFunctionsService.stopSpinner();
          }
        }
      });
  }

  private mapResponseToUserData(reviewList: ReviewDetails[]): ReviewDetails[] {
    return reviewList.map((item: ReviewDetails) => ({
      ...item,
      status: item.status || Constants.HYPHEN,
      request: item.request || Constants.HYPHEN,
      tdc: item.tdc,
      gm: this.commonFunctionsService.appendSymbolToText(item.gm, Constants.PERCENT) || Constants.HYPHEN,
      requestor: item.requestor || Constants.HYPHEN,
      reviewer: item.reviewer || Constants.HYPHEN,
      originalReviewer: item.originalReviewer || Constants.HYPHEN,
      openDate: item.openDate ? new Date(item.openDate) : Constants.EMPTY_STRING,
      closeDate: item.closeDate ? new Date(item.closeDate) : Constants.EMPTY_STRING,
      estimateID: item.estimateID || Constants.HYPHEN,
      requiredBy: item.requiredBy ? new Date(item.requiredBy) : Constants.EMPTY_STRING,
      business: item.business || Constants.HYPHEN,
      segment: item.segment || Constants.HYPHEN,
      subSegment: item.subSegment || Constants.HYPHEN,
      estimateSubSegmentType: item.estimateSubSegmentType || Constants.ZERO,
    }));
  }

  updateRequestObjFromForm() {
    let tempStatusArray = []
    if (this.filterForm.get('status.open')?.value) tempStatusArray.push(Status.Open);
    if (this.filterForm.get('status.approved')?.value) tempStatusArray.push(Status.Approved);
    if (this.filterForm.get('status.denied')?.value) tempStatusArray.push(Status.Denied);
    this.requestObj.status = tempStatusArray.join(Constants.COMMA);

    let tempBusinessArray = []
    if (this.filterForm.get('business.fire')?.value) tempBusinessArray.push(Business.Fire);
    if (this.filterForm.get('business.security')?.value) tempBusinessArray.push(Business.Security);
    if (this.filterForm.get('business.automation')?.value) tempBusinessArray.push(Business.Automation);
    if (this.filterForm.get('business.electrical')?.value) tempBusinessArray.push(Business.Electrical);
    if (this.filterForm.get('business.mechanical')?.value) tempBusinessArray.push(Business.Mechanical);
    this.requestObj.business_44OP = tempBusinessArray.join(',');

    let tempTypeArray = []
    if (this.filterForm.get('type.44OP')?.value) tempTypeArray.push(EstimateType.OP44);
    if (this.filterForm.get('type.2600')?.value) tempTypeArray.push(EstimateType.Type2600);
    if (this.filterForm.get('type.5200')?.value) tempTypeArray.push(EstimateType.Type5200);
    this.requestObj.estimateType = tempTypeArray.join(Constants.COMMA);

    this.requestObj.isallreviews = this.filterForm.get('reviewer')?.value == 'all'
    this.requestObj.searchText = this.filterForm.get('searchText')?.value;
    this.requestObj.searchType = this.filterForm.get('searchText')?.value ? this.filterForm.get('searchType')?.value : SearchType.None;
  }

  updateRequestObjForLinkOpen(estimateId: string) {
    this.requestObj.status = Object.values(Status).join(',');
    this.requestObj.business_44OP = Object.values(Business).join(',');
    this.requestObj.estimateType = Object.values(EstimateType).join(',');
    this.requestObj.isallreviews = true;
    this.requestObj.pageNo = Constants.ONE;
    this.requestObj.searchText = estimateId;
    this.requestObj.searchType = SearchType.EstimateID;
    this.patchRequestObjValuesInForm();
    const searchTextControl = this.filterForm.get('searchText');
    searchTextControl?.markAsDirty();
  }

  patchRequestObjValuesInForm() {
    this.totalNumberOfReviews = this.totalNumberOfReviews || Constants.MINUS_ONE;
    this.filterForm.patchValue({
      status: {
        open: this.requestObj.status.includes(Status.Open),
        approved: this.requestObj.status.includes(Status.Approved),
        denied: this.requestObj.status.includes(Status.Denied),
      },
      type: {
        '44OP': this.requestObj.estimateType.includes(EstimateType.OP44),
        '2600': this.requestObj.estimateType.includes(EstimateType.Type2600),
        '5200': this.requestObj.estimateType.includes(EstimateType.Type5200),
      },
      business: {
        fire: this.requestObj.business_44OP.includes(Business.Fire),
        security: this.requestObj.business_44OP.includes(Business.Security),
        automation: this.requestObj.business_44OP.includes(Business.Automation),
        electrical: this.requestObj.business_44OP.includes(Business.Electrical),
        mechanical: this.requestObj.business_44OP.includes(Business.Mechanical)
      },
      reviewer: this.requestObj.isallreviews ? 'all' : 'my',
      searchType: this.requestObj.searchType == Constants.MINUS_ONE ? SearchType.EstimateID : this.requestObj.searchType,
      searchText: this.requestObj.searchText,
    });
  }

  isAtleastOneCheckedStatus(formControlName: string) {
    let oneChecked: boolean
    if (formControlName === 'open') {
      oneChecked = this.filterForm.get('status.approved')?.value || this.filterForm.get('status.denied')?.value;
      if (!oneChecked) {
        this.filterForm.get('status.open')?.patchValue(true);
        return false;
      } return true
    }
    else if (formControlName === 'approved') {
      oneChecked = this.filterForm.get('status.open')?.value || this.filterForm.get('status.denied')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ status: { approved: true } });
        return false;
      } return true
    }
    else {
      oneChecked = this.filterForm.get('status.open')?.value || this.filterForm.get('status.approved')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ status: { denied: true } });
        return false;
      } return true
    }
  }

  isAtleastOneCheckedType(formControlName: string) {
    let oneChecked: boolean
    if (formControlName === '44OP') {
      oneChecked = this.filterForm.get('type.2600')?.value || this.filterForm.get('type.5200')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ type: { '44OP': true } });
        return false;
      } return true
    }
    else if (formControlName === '2600') {
      oneChecked = this.filterForm.get('type.44OP')?.value || this.filterForm.get('type.5200')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ type: { '2600': true } });
        return false;
      } return true
    }
    else { //5200
      oneChecked = this.filterForm.get('type.44OP')?.value || this.filterForm.get('type.2600')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ type: { '5200': true } });
        return false;
      } return true
    }
  }

  isAtleastOneCheckedBusiness(formControlName: string) {
    let oneChecked: boolean
    if (formControlName === 'fire') {
      oneChecked = this.filterForm.get('business.security')?.value || this.filterForm.get('business.automation')?.value || this.filterForm.get('business.electrical')?.value || this.filterForm.get('business.mechanical')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ business: { fire: true } });
        return false;
      } return true
    }
    else if (formControlName === 'security') {
      oneChecked = this.filterForm.get('business.fire')?.value || this.filterForm.get('business.automation')?.value || this.filterForm.get('business.electrical')?.value || this.filterForm.get('business.mechanical')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ business: { security: true } });
        return false;
      } return true
    }
    else if (formControlName === 'automation') {
      oneChecked = this.filterForm.get('business.fire')?.value || this.filterForm.get('business.security')?.value || this.filterForm.get('business.electrical')?.value || this.filterForm.get('business.mechanical')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ business: { automation: true } });
        return false;
      } return true
    } else if (formControlName === 'mechanical') {
      oneChecked = this.filterForm.get('business.fire')?.value || this.filterForm.get('business.security')?.value || this.filterForm.get('business.electrical')?.value || this.filterForm.get('business.automation')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ business: { mechanical: true } });
        return false;
      } return true
    }
    else { //electrical
      oneChecked = this.filterForm.get('business.fire')?.value || this.filterForm.get('business.security')?.value || this.filterForm.get('business.automation')?.value || this.filterForm.get('business.mechanical')?.value;
      if (!oneChecked) {
        this.filterForm.patchValue({ business: { electrical: true } });
        return false;
      } return true
    }
  }

  unpauseReview() {
    this.isRefreshPaused = false;
    this.notificationService.showInfo(
      this.translate.instant('NOTIFICATIONS_MESSAGES.REVIEW_SYNC_TITLE'), 
      this.translate.instant('NOTIFICATIONS_MESSAGES.REVIEW_SYNC_RESUME_MESSAGE'));
  }

  pauseReview() {
    this.isRefreshPaused = true;
    this.notificationService.showInfo(
      this.translate.instant('NOTIFICATIONS_MESSAGES.REVIEW_SYNC_TITLE'), 
      this.translate.instant('NOTIFICATIONS_MESSAGES.REVIEW_SYNC_PAUSE_MESSAGE'));
  }
  //#endregion

  //#region OPERATIONS
  updateTRRStatus() {
    const dialogRef = this.dialog.open(TrrDialogComponent, {
      data: {
        selectedRadioOption: this.mapTRRStatusToRadioButtonSelection(this.selectedReview.trrStatus),
        trrThresholds: this.trrThresholds
      },
      disableClose: true
    });

    dialogRef.afterClosed().subscribe((result: TRRDialogOutput) => {
      if (result && result instanceof TRRDialogOutput && result.button === Constants.OK) {
        const trrUpdateRequest = new CostReviewUpdateTRRStatusRequest();
        trrUpdateRequest.estimateId = this.selectedReview.estimateID;
        trrUpdateRequest.originalReviewerId = this.selectedReview.originalReviewerID;
        trrUpdateRequest.trrStatus = this.mapRadioButtonSelectionToTRRStatus(result.selectedRadioOption);

        this.apiMethodsService.updateReviewTRRStatus(trrUpdateRequest)
          .pipe(take(Constants.ONE))
          .subscribe({
            next: (result) => {
              if (result) {
                if (result.isTRRUpdated) {
                  this.selectedReview.trrStatus = trrUpdateRequest?.trrStatus ?? Constants.EMPTY_STRING;
                }
                else {
                  this.showCostReviewErrorMessage(result.errorCode, result.currentReviewer, true);
                }
              }
              else {
                this.showCostReviewErrorMessage(Constants.ZERO, Constants.EMPTY_STRING, false);
              }
            },
            error: (err: APIErrors) => {
              this.showCostReviewErrorMessage(err.errorCode, Constants.EMPTY_STRING, false);
            }
          });
      } else {
      }
    });
  }

  private mapRadioButtonSelectionToTRRStatus(selectionOption: string): string {
    switch (selectionOption) {
      case Constants.ZERO_STR:
        return Constants.YES;
      case Constants.ONE_STR:
        return Constants.NO;
      case Constants.TWO_STR:
        return Constants.NA;
      default:
        return Constants.NA;
    }
  }

  private mapTRRStatusToRadioButtonSelection(trrStatus: string): string {
    switch (trrStatus) {
      case Constants.YES:
        return Constants.ZERO_STR;
      case Constants.NO:
        return Constants.ONE_STR;
      case Constants.NA:
        return Constants.TWO_STR;
      default:
        return Constants.TWO_STR;
    }
  }

  closeReview() {

    this.scheduleReviewDataRefresh();
    this.isRefreshPaused = false;
    this.selectedReview = new ReviewDetails();
    this.router.navigate([Constants.REVIEW_ROUTE]);
  }

  checkTRRDisabled(): boolean {
    if (!this.selectedReview ||
      Object.keys(this.selectedReview).length === Constants.ZERO ||
      this.excludedEstimateSubSegmentTypesforTRR.includes(this.selectedReview.estimateSubSegmentType) ||
      this.selectedReview.status != Constants.OPEN
    ) {
      return true;
    }
    if (this.selectedReview.approvedSellPrice > this.getThresholdForSelectedItem(this.selectedReview)) {
      return false;
    }
    return true;
  }

  private getThresholdForSelectedItem(selectedItem: any): number {
    switch (selectedItem.business) {
      case Constants.FIRE:
        if (selectedItem.installType == Constants.TWO) {
          return this.trrThresholds.firePartsAndSmarts;
        }
        else {
          return this.trrThresholds.fire;
        }
      case Constants.SECURITY:
        return this.trrThresholds.security;
      case Constants.AUTOMATION:
        return this.trrThresholds.automation;
      case Constants.ELECTRICAL:
        return this.trrThresholds.electrical;
      case Constants.MECHANICAL:
        return this.trrThresholds.mechanical;
      default:
        return Constants.ZERO;
    }
  }

  approveReview(
    isReviewListPage: boolean,
    currentUser: UserInfo
  ) {
    const requestObj: CostReviewApproveRequest = {
      estimateId: this.selectedReview.estimateID,
      originalReviewerId: this.selectedReview.originalReviewerID,
    };
    this.commonFunctionsService.startSpinner();
    this.apiMethodsService.approveCostReview(requestObj)
      .pipe(take(Constants.ONE))
      .subscribe({
        next: (result) => {
          if (result) {
            if (result.isApproved) {
              let estimateName = this.selectedReview.request || this.reviewHeaderData.estimateName;
              if (isReviewListPage) {
                this.refreshReview(true);
              }
              this.selectedReview.status = Constants.APPROVED;
              this.reviewHeaderData.status = Constants.APPROVED;

              this.commonFunctionsService.stopSpinner();
              this.notificationService.showSuccess(
                this.translate.instant('USERS_TAB.COST_REVIEW'), this.translate.instant('NOTIFICATIONS_MESSAGES.APPROVED_SUCCESSFULLY')
              );
              const emailAttributes = new EmailAttributes();
              emailAttributes.to = result.emailAttributes?.to;
              emailAttributes.cc = result.emailAttributes?.cc;
              this.openEmailPopup(emailAttributes, Constants.EMPTY_STRING, true, currentUser, estimateName);
            } else {
              this.showCostReviewErrorMessage(result.errorCode, result.currentReviewer, true);
            }
          } else {
            this.showCostReviewErrorMessage(Constants.ZERO, Constants.EMPTY_STRING, true);
          }
        },
        error: () => {
          this.showCostReviewErrorMessage(Constants.ZERO, Constants.EMPTY_STRING, true);
        },
      });
  }

  refreshReview(isReviewListPage: boolean = false) {
    this.resetRequestReviewData(Constants.ZERO, !isReviewListPage);
  }

  resetRequestReviewData(currentPageIndex: number, navigateToReviewlist: boolean = false) {
    this.currentPageIndex = currentPageIndex;
    this.updateRequestObjFromForm();
    if (navigateToReviewlist) {
      this.router.navigate([Constants.REVIEW_ROUTE]);
    } else {
      this.requestReviewData(this.currentPageIndex);
    }
  }

  private openEmailPopup(emailAttributes: EmailAttributes, comments: string, isApproved: boolean, currentUser: UserInfo, estimateName: string) {
    if (emailAttributes && emailAttributes.to && currentUser) {
      if (isApproved) {
        this.openApproveEmailPopup(emailAttributes.to, estimateName, this.selectedReview.estimateID, currentUser.fullName, emailAttributes.cc);
      }
      else {
        this.openDenyEmailPopup(emailAttributes.to, estimateName, this.selectedReview.estimateID, currentUser.fullName, comments, emailAttributes.cc);
      }
    }
  }

  private openApproveEmailPopup(requestorEmailId: string, reviewName: string, estimateId: string, userFullName: string, ccEmailId?: string[]) {
    const mailSubject = `${Constants.REVIEW_APPROVED_MAIL_SUBJECT}${Constants.REVIEW_MAIL_SUBJECT_HYPHEN}${reviewName}`;
    let mailBody = `${Constants.REVIEW_APPROVED_MAIL_BODY_LINE_1}${reviewName} (${estimateId})${Constants.REVIEW_APPROVED_MAIL_BODY_LINE_2}${userFullName}`;

    mailBody = mailBody.replace(/\r\n/g, Constants.LINE_FEED);
    mailBody = mailBody.replace(/&/g, Constants.AMPERCENT_FEED);

    let url = `mailto:${requestorEmailId}?Subject=${mailSubject}&Body=${mailBody}`;

    if (ccEmailId && ccEmailId.length > Constants.ZERO) {
      const ccEmails = ccEmailId.join('; ');
      url += `&CC=${ccEmails}`;
    }
    window.open(url, '_blank');
  }

  private openDenyEmailPopup(
    requestorEmailId: string,
    reviewName: string,
    estimateId: string,
    userFullName: string,
    comments: string,
    ccEmailId?: string[]
  ) {
    const mailSubject = `${Constants.REVIEW_DENIED_MAIL_SUBJECT}${Constants.REVIEW_MAIL_SUBJECT_HYPHEN}${reviewName}`;
    let mailBody = `${Constants.REVIEW_DENIED_MAIL_BODY_LINE_1}${reviewName} (${estimateId})${Constants.REVIEW_DENIED_MAIL_BODY_LINE_2}${comments ? `${Constants.REVIEW_DENIED_MAIL_BODY_LINE_3}${comments}` : ""
      }${Constants.REVIEW_DENIED_MAIL_BODY_LINE_4}${userFullName}`;

    // Calculate the length of the URL components
    const { baseLength, ccLength, encodedMailBody } = this.generateMailUrlComponents(requestorEmailId, mailSubject, mailBody, ccEmailId);

    // Check if truncation is needed
    const totalLength = baseLength + encodedMailBody.length + ccLength;
    if (totalLength > Constants.MAX_MAILTO_URL_LENGTH) {
      const maxCommentLength = comments.length - (totalLength - Constants.MAX_MAILTO_URL_LENGTH);
      if (maxCommentLength > Constants.ZERO) {
        comments = comments.substring(Constants.ZERO, maxCommentLength) + "...";
        mailBody = `${Constants.REVIEW_DENIED_MAIL_BODY_LINE_1}${reviewName} (${estimateId})${Constants.REVIEW_DENIED_MAIL_BODY_LINE_2}${Constants.REVIEW_DENIED_MAIL_BODY_LINE_3}${comments}${Constants.REVIEW_DENIED_MAIL_BODY_LINE_4}${userFullName}`;
      }
    }

    // Generate the final URL
    const url = this.generateMailUrl(requestorEmailId, mailSubject, mailBody, ccEmailId);
    window.open(url, "_blank");
  }


  private generateMailUrlComponents(
    requestorEmailId: string,
    mailSubject: string,
    mailBody: string,
    ccEmailId?: string[]
  ): { baseLength: number; ccLength: number; encodedMailBody: string } {
    const encodedMailBody = mailBody.replace(/\r\n/g, Constants.LINE_FEED).replace(/&/g, Constants.AMPERCENT_FEED);
    const baseLength = `mailto:${requestorEmailId}?Subject=${mailSubject}&Body=`.length;
    const ccLength = ccEmailId && ccEmailId.length ? `&CC=${ccEmailId.join('; ')}`.length : Constants.ZERO;
    return { baseLength, ccLength, encodedMailBody };
  }

  private generateMailUrl(
    requestorEmailId: string,
    mailSubject: string,
    mailBody: string,
    ccEmailId?: string[]
  ): string {
    const { encodedMailBody } = this.generateMailUrlComponents(requestorEmailId, mailSubject, mailBody, ccEmailId);
    let url = `mailto:${requestorEmailId}?Subject=${mailSubject}&Body=${encodedMailBody}`;
    if (ccEmailId && ccEmailId.length) {
      const ccEmails = ccEmailId.join('; ');
      url += `&CC=${ccEmails}`;
    }
    return url;
  }

  checkApproveButtonDisabled(): boolean {
    return (!this.selectedReview ||
      !this.selectedReview.status ||
      this.selectedReview.status != Constants.OPEN ||
      (!this.checkTRRDisabled() && (this.selectedReview.trrStatus === Constants.NA || this.selectedReview.trrStatus === Constants.EMPTY_STRING)));
  }

  denyReview(isReviewListPage: boolean, currentUser: UserInfo) {
    const dialogRef = this.dialog.open(DenyDialogComponent, {
      width: '550px',
      disableClose: true,
    });
    let comments: string;
    dialogRef.afterClosed().subscribe(result => {
      if (result && result instanceof DenyDialogOutput) {
        if (result.button === Constants.OK) {
          comments = result.comments;
          this.sendDenyRequest(comments, isReviewListPage, currentUser);
        }
      }
    });
  }

  private sendDenyRequest(comments: string, isReviewListPage: boolean, currentUser: UserInfo) {
    let denyRequest = new CostReviewDenyRequest();
    denyRequest.estimateId = this.selectedReview.estimateID;
    denyRequest.denyComments = comments;
    denyRequest.originalReviewerId = this.selectedReview.originalReviewerID;
    this.commonFunctionsService.startSpinner();
    this.apiMethodsService.denyCostReview(denyRequest)
      .pipe(take(Constants.ONE))
      .subscribe({
        next: (result) => {
          if (result) {
            if (result.isDenied) {
              let estimateName = this.selectedReview.request || this.reviewHeaderData.estimateName;
              if (isReviewListPage) {
                this.refreshReview(true);
              }
              this.selectedReview.status = Constants.DENIED;
              this.reviewHeaderData.status = Constants.DENIED;

              this.commonFunctionsService.stopSpinner();
              this.notificationService.showSuccess(
                this.translate.instant('USERS_TAB.COST_REVIEW'), this.translate.instant('NOTIFICATIONS_MESSAGES.DENIED_SUCCESSFULLY')
              );
              let emailAttributes = new EmailAttributes();
              emailAttributes.to = result.emailAttributes?.to;
              emailAttributes.cc = result.emailAttributes?.cc;
              this.openEmailPopup(emailAttributes, comments, false, currentUser, estimateName);
            }
            else {
              this.showCostReviewErrorMessage(result.errorCode, result.currentReviewer, true);
            }
          }
          else {
            this.showCostReviewErrorMessage(Constants.ZERO, Constants.EMPTY_STRING, true);
          }
        },
        error: () => {
          this.showCostReviewErrorMessage(Constants.ZERO, Constants.EMPTY_STRING, true);
        }
      });
  }

  checkDenyButtonDisabled(): boolean {
    return !this.selectedReview.status || this.selectedReview.status !== Constants.OPEN;
  }

  exportTableData() {
    this.commonFunctionsService.startSpinner();
    this.apiMethodsService.exportCostReviewList(this.requestObj)
      .pipe(take(Constants.ONE))
      .subscribe({
        next: (response: Blob) => {
          const url = window.URL.createObjectURL(response);
          const a = document.createElement('a');
          a.setAttribute('style', 'display:none;');
          a.href = url;
          a.download = 'ReviewList_' + this.datePipe.transform(new Date(), 'ddMMyyyy_HH_mm') + '.csv';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          this.commonFunctionsService.stopSpinner();
        }, error: (msg: any) => {
          this.messageService.showErrorMessage(
            this.translate.instant('NOTIFICATIONS_MESSAGES.MESSAGEBOX_EXPORT_TO_CSV_HEADER'), 
            this.translate.instant('NOTIFICATIONS_MESSAGES.MESSAGEBOX_APPROVE_DENY_ERROR_0')
          );
          this.commonFunctionsService.stopSpinner();
        }
      });
  }
  //#endregion

  //#region OPEN

  setCostReviewData(data: CostReviewDatabaseRecord) {
    this.costReviewDetails = data;
    this.newCostReviewObject = data.newReviewObject as NewCostReviewAttributes_SOL;
    this.approvedSellsPrice = data.approvedSellPrice || 0;
    this.dataRefreshed$.next();
  }
  
  getBusiness(): string {
    return this._business;
  }

  setBusiness(value: string): void {
    this._business = value;
  }

  getOrCreateReviewObjectForBusiness(business: string): CostReviewAttributes | CostReviewFireEnUsAttributes | CostReviewSecurityEnUsAttributes | null  {
    if (this.costReviewDetails.reviewObject) {
      return this.costReviewDetails.reviewObject as CostReviewAttributes | CostReviewFireEnUsAttributes | CostReviewSecurityEnUsAttributes;
    }

    switch (business) {
      case Constants.BUSINESS_AUTOMATION:
      case Constants.BUSINESS_ELECTRICAL:
      case Constants.BUSINESS_MECHANICAL:
        this.costReviewDetails.reviewObject = new CostReviewAttributes();
        break;

      case Constants.BUSINESS_FIRE:
        this.costReviewDetails.reviewObject = new CostReviewFireEnUsAttributes();
        break;

      case Constants.BUSINESS_SECURITY:
        this.costReviewDetails.reviewObject = new CostReviewSecurityEnUsAttributes();
        break;

      default:
        return null;
    }

    return this.costReviewDetails.reviewObject as CostReviewAttributes | CostReviewFireEnUsAttributes | CostReviewSecurityEnUsAttributes;
  }

  setCostReviewParameterData(data: CostReviewParameters[]) {
    this.costReviewParameters = [...data];
  }

  /**
   * Notifies the host app that a specific action-needed field was modified.
   *
   * Call this from any tab component after updating one of the 13
   * action-needed-relevant fields on `costReviewDetails` so the app
   * can apply a targeted update and re-evaluate action-needed visibility.
   *
   * Request a form sync first so any dependent values still sitting in the
   * active tab form are pushed into the shared review object before the app
   * recalculates action-needed state.
   *
   * @param field  The `ActionNeededField` that was modified.
   * @param value  The new value.
   */
  notifyFieldChange(field: ActionNeededField, value: string | number | boolean): void {
    this.reviewDetailTabsService.requestReviewFormDataSync();
    this.reviewDetailTabsService.notifyActionNeededFieldChanged(field, value);
  }

  GetCostReviewMasterData(cultureId: number) {
    this.commonDataService.allMasterDataValues
      .subscribe((masteData: AllMasterData | null) => {
        if (masteData) {
          this.costReviewParameters = cultureId === CULTURE_ID.FR_CA ?
            masteData.costReviewParametersFR :
            masteData.costReviewParameters;
          this.trrThresholds = masteData.trrThresholds;
        }
      });
  }

  //#endregion

  //#region COMMON

  getEnUsForFrCaDisciplineName(discipline: string): string {

    let disciplineEnUs: string = discipline;
    switch (discipline) {
      case Constants.AUTOMATION_FRCA:
        disciplineEnUs = Constants.AUTOMATION;
        break;
      case Constants.ELECTRICAL_FRCA:
        disciplineEnUs = Constants.ELECTRICAL;
        break;
      case Constants.ENERGY_FRCA:
        disciplineEnUs = Constants.ENERGY;
        break;
      case Constants.FIRE_FRCA:
        disciplineEnUs = Constants.FIRE;
        break;
      case Constants.MECHANICAL_FRCA:
        disciplineEnUs = Constants.MECHANICAL;
        break;
      case Constants.SECURITY_FRCA:
        disciplineEnUs = Constants.SECURITY;
        break;
      case Constants.SPRINKLER_FRCA:
        disciplineEnUs = Constants.SPRINKLER;
        break;
      case Constants.ALLDISCIPLINE_FRCA:
        disciplineEnUs = Constants.ALL_DISCIPLINES;
        break;
      default:
        disciplineEnUs = discipline;
        break;
    }
    return disciplineEnUs;
  }

  getCostReviewParametersDescriptionForArray(parameter: string, ids: Array<string | number> | undefined): string {
    let description = '';
    if (ids && ids.length > 0) {
      ids.forEach((id, index) => {
        let paramId = `${parameter}_${id}`;
        let costReviewParameter = this.costReviewParameters.find(param =>
          param.parameterID === paramId
        );
        description += costReviewParameter ? costReviewParameter.description : '';
        if (index < ids.length - 1) {
          description += ', ';
        }
      });
    }
    return description;
  }

  getCostReviewParametersDescription(parameter: string, id: number | string | undefined): string {
    if (id !== undefined && id !== null && id !== '') {
      let paramId = `${parameter}_${id}`;
      let costReviewParameter = this.costReviewParameters.find(param =>
        param.parameterID === paramId
      );
      return costReviewParameter ? costReviewParameter.description : '';
    }
    return '';

  }

  getDropdownValues(attribute: string): { label: string; value: string }[] {
    if (!attribute) {
      return [];
    }

    return this.costReviewParameters
      .filter((parameter) => parameter.parameterID.includes(attribute))
      .map((parameter) => ({
        value: parameter.key.toString(),
        label: parameter.description
      }));
  }

  getCostReviewParametersCode(parameter: string, description: string): number | undefined {
    if (!description) return undefined;
    const prefix = `${parameter}_`;
    const param = this.costReviewParameters.find(p =>
      p.parameterID.startsWith(prefix) && p.description === description
    );
    if (param) {
      const code = Number(param.parameterID.substring(prefix.length));
      return isNaN(code) ? undefined : code;
    }
    return undefined;
  }

  getCostReviewParametersCodesForArray(parameter: string, descriptions: string): number[] {
    if (!descriptions) return [];
    return descriptions.split(', ')
      .map(desc => this.getCostReviewParametersCode(parameter, desc.trim()))
      .filter((code): code is number => code !== undefined);
  }

  showCostReviewErrorMessage(
    errorCode: number,
    currentReviewer: string,
    isReviewOperation: boolean = true): void {

    let errorMessage = Constants.MESSAGEBOX_APPROVE_DENY_ERROR_PREFIX;

    switch (errorCode) {
      case Constants.COST_REVIEW_OPERATIONS_NETWORK_ERROR:
        errorMessage = Constants.MESSAGEBOX_APPROVE_DENY_ERROR_0;
        break;
      case Constants.COST_REVIEW_OPERATIONS_ERROR_APPROVED_DENIED:
        if (!currentReviewer) {
          errorMessage += Constants.MESSAGEBOX_APPROVE_DENY_ERROR_MULTIPLE_REVIEWER_2;
        } else {
          errorMessage += Constants.MESSAGEBOX_APPROVE_DENY_ERROR_2.replace('{0}', currentReviewer);
        }
        break;
      case Constants.COST_REVIEW_OPERATIONS_ERROR_REASSIGNED:
        if (!currentReviewer) {
          errorMessage += Constants.MESSAGEBOX_APPROVE_DENY_ERROR_MULTIPLE_REVIEWER_3;
        } else {
          errorMessage += Constants.MESSAGEBOX_APPROVE_DENY_ERROR_3.replace('{0}', currentReviewer);
        }
        break;
      case Constants.COST_REVIEW_OPERATIONS_ERROR_RECALLED:
        errorMessage += Constants.MESSAGEBOX_APPROVE_DENY_ERROR_4;
        break;
      default:
        break;
    }

    const dialogData = new DialogInputBase();
    dialogData.message = errorMessage;
    dialogData.title = isReviewOperation
      ? this.translate.instant('NOTIFICATIONS_MESSAGES.MESSAGEBOX_APPROVE_DENY_HEADER')
      : this.translate.instant('NOTIFICATIONS_MESSAGES.MESSAGEBOX_UNKNOWN_ERROR_HEADER');

    this.messageService.showErrorMessage(dialogData.title, dialogData.message);
  }

  //#endregion

  onPageSizeLevelWiseChange(newSize: string, paginator: MatPaginator | null, event: any) {
    const sizeToSet = Number(newSize) > Constants.ZERO ? Number(newSize) : Constants.TEN;
    if (Number(event.target.value) === Constants.ZERO) {
      event.target.value = sizeToSet;
    }
    (paginator as any)._changePageSize(sizeToSet);
  }

  convertToEstimateType(optionalEstimateType: CostReviewEstimateType | undefined): CostReviewEstimateType {
    switch (optionalEstimateType) {
      case CostReviewEstimateType.EstimateLess50K:
        return CostReviewEstimateType.EstimateLess50K;
      case CostReviewEstimateType.Estimate50_150K:
        return CostReviewEstimateType.Estimate50_150K;
      case CostReviewEstimateType.EstimateGreater150K:
        return CostReviewEstimateType.EstimateGreater150K;
      default:
        return CostReviewEstimateType.None;
    }
  }
}
